import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import qs from 'qs';
import 'scorm-again/dist/scorm-again';

import { AnyObject, AppProps, GetSCORMCMI, ISCORMPlayerParameter } from './App.interfaces';

import { errorGenerator, sessionTimeCounter, sumTwoTimes } from './Utils';

import './App.scss';

const startSession = new Date();

function App(props: AppProps) {
  const [data, setData] = useState<ISCORMPlayerParameter | null>(null);
  const [isInitialLoadFinish, setIsInitialLoadFinish] = useState(false);

  // event handling
  const getScormCMI = useCallback((event: GetSCORMCMI) => {
    if (!data) return;

    const { scormContent } = data;

    const cmiData = JSON.parse(JSON.stringify(window.API.cmi));

    let dataSource = "scorm-player";

    if (event === "LMSCommit") dataSource = "scorm-player-commit";
    if (event === "GetData") dataSource = "scorm-player-get-data";

    const payload = {
      dataSource,
      cmiData: {
        cmi: {
          ...cmiData,
        }
      },
    }

    const now = new Date();
    const sessionTime = sessionTimeCounter(startSession.getTime(), now.getTime());
    const prevTotalTime = scormContent?.cmi?.core?.total_time || "00:00:00";
    const totalTime = sumTwoTimes(prevTotalTime, sessionTime);

    payload.cmiData.cmi.core.session_time = sessionTime;
    payload.cmiData.cmi.core.total_time = totalTime;

    return payload;
  }, [data]);

  const sendData = useCallback((event: GetSCORMCMI) => {
    if (!data) return;

    const { platform, parentOrigin } = data;

    const payload = getScormCMI(event);

    if (platform === 'web') {
      if (parentOrigin) window.parent.postMessage(payload, parentOrigin);
      return;
    }

    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
  }, [data, getScormCMI]);

  const onFinish = useCallback(() => {
    sendData("LMSFinish");
  }, [sendData]);

  const onCommit = useCallback(() => {
    sendData("LMSCommit");
  }, [sendData]);

  // 1st initial load, data parsing
  useEffect(() => {
    const originSearch = props.location.search;

    const params = qs.parse(originSearch, { ignoreQueryPrefix: true }) as { q?: string };

    const query = params.q;

    if (!query) return;

    const decoded = decodeURIComponent(query);
    const decrypted = atob(decoded);

    try {
      const parsed: ISCORMPlayerParameter = JSON.parse(decrypted);

      setData(parsed);
    } catch (error) {
      console.log(errorGenerator(error));

      setIsInitialLoadFinish(true);
    }
  }, [props.location.search]);

  // 2nd initial load, init window API
  useEffect(() => {
    if (!data) return;

    const { scormContent, resourceParameter } = data;

    const defaultConfig: AnyObject = {
      dataCommitFormat: 'json',
      commitRequestDataType: 'application/json;charset=UTF-8',
      logLevel: 5,
      alwaysSendTotalTime: true,
      lmsCommitUrl: resourceParameter.lmsCommitUrl,
      xhrHeaders: {
        ...resourceParameter.commitHeader,
      },
      responseHandler: (result: AnyObject) => {
        console.log("response: ", JSON.stringify(result));
      }
    };

    window.API = new window.Scorm12API({
      ...defaultConfig,
    });

    window.API.on("LMSFinish", () => {
      console.log("LMSFinish")
      onFinish();
    });

    window.API.on("LMSCommit", () => {
      const cmiData = JSON.parse(JSON.stringify(window.API.cmi));
      console.log("LMSCommit: ", cmiData);

      onCommit();
    })

    window.API.loadFromJSON(scormContent, '');

    setIsInitialLoadFinish(true);

    return () => {
      window.API.clear("LMSFinish");
      window.API.clear("LMSCommit");
    }
  }, [onFinish, onCommit, data]);

  // attach listener
  const doUnload = useCallback(() => {
    const elm = document.getElementById("scorm-content");

    if (!elm) return;

    elm.remove();
  }, []);

  const listener = useCallback((event: MessageEvent) => {
    const eventData = event.data;

    if (eventData === 'exit-scorm-player') {
      doUnload();
      return;
    }

    if (eventData === 'get-scorm-data') {
      sendData("GetData");
      return;
    }
  }, [doUnload, sendData]);

  useEffect(() => {
    if (!isInitialLoadFinish || !data) return;

    const { platform } = data;

    if (platform === 'web' || platform === 'ios') window.addEventListener("message", listener);

    if (platform === 'android') {
      const elm = document.getElementById("scorm-content");

      if (!elm) return;

      // @ts-ignore
      document.addEventListener("message", listener)
    }

    return () => {
      if (platform === 'web' || platform === 'ios') window.removeEventListener("message", listener);

      if (platform === 'android') {
        const elm = document.getElementById("scorm-content");

        if (!elm) return;

        // @ts-ignore
        document.removeEventListener("message", listener)
      }
    }
  }, [isInitialLoadFinish, data, listener])

  if (!isInitialLoadFinish) return (
    <div className="scorm-player">
      <div className="full-size">
        Loading content...
      </div>
    </div>
  )

  if (!data) return (
    <div className="scorm-player">
      <div className="full-size">
        Something went wrong...
      </div>
    </div>
  )

  return (
    <div className="scorm-player">
      <iframe
        id="scorm-content"
        src={data.contentPath}
        className="iframe-style"
        name="scorm-frame"
        title="scorm-player-frame"
        scrolling="auto"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

export default withRouter(App);
